/**
 * SettingsDialog - Modal overlay for game settings (theme, audio, party pacing).
 *
 * Account-related cards (email verification, passkey register, change
 * password) moved to the dedicated AccountModal. Notification +
 * Layout-reset sections removed pending product readiness — restore
 * from git history if/when those flows ship.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Bot,
  Clapperboard,
  DoorOpen,
  Monitor,
  Moon,
  Sun,
  Unplug,
  UserMinus,
  UserRound,
  Users,
  Volume2,
  VolumeX,
  Settings,
  Timer,
  Zap,
} from 'lucide-react';
import type { FriendInfo, PacingMode } from '@danda/shared';
import { apiClient, type DropBehavior } from '@/lib/api';
import { useSoundtrackEngine, useSoundtrackState } from '@/lib/audio/use-soundtrack';
import { useTheme } from '@/hooks/useTheme';
import { useLobbyStore, useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { websocketService } from '@/lib/websocket-service';
import { toast } from '@/lib/toast';

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const { theme, themePreference, setTheme } = useTheme();
  const soundtrackEngine = useSoundtrackEngine();
  const { volume, audioOn } = useSoundtrackState();

  // Party pacing (leader-controlled, PRE-GAME only). Hidden entirely when the
  // user has no party; visible but read-only for non-leaders. Locked once an
  // adventure is live (the server rejects a change with SESSION_ACTIVE), and
  // the setter rides the LOBBY socket which only /lobby connects, so outside
  // the lobby (e.g. opened from the dashboard) it stays read-only with a hint
  // instead of silently queueing the frame.
  const selectedCharacter = useSelectedCharacter();
  const parties = useLobbyStore((state) => state.parties);
  const lobbyOnline = useLobbyStore((state) => state.isOnline);
  const wsPartyRaw = selectedCharacter
    ? (parties.find((p) => p.memberCharacterIds.includes(selectedCharacter.id)) ?? null)
    : null;
  // Only trust wsParty while the live lobby socket is actually connected:
  // lobbyStore.parties is never cleared on disconnect, so leaving /lobby for
  // /dashboard would otherwise keep showing this stale snapshot forever
  // (masking pacing changes made via REST here, or from another device).
  const wsParty = lobbyOnline ? wsPartyRaw : null;

  // REST fallback: the live lobby socket only connects on /lobby, so wsParty
  // is always null elsewhere (dashboard, in-game). Mirrors the mobile pacing
  // selector, which already reads pacingMode off this same /api/party
  // response instead of requiring a live socket (menu_panel.dart).
  const [restParty, setRestParty] = useState<{
    id: string;
    leaderCharacterId?: string;
    pacingMode?: PacingMode;
    activeGameSessionId?: string;
    leaderName?: string;
  } | null>(null);
  const [myCharId, setMyCharId] = useState<string | null>(null);
  // Optimistic UI for the REST pacing path: applied immediately on click,
  // reverted if apiClient.setPartyPacing rejects (mirrors handleDropBehavior).
  const [restPacingOverride, setRestPacingOverride] = useState<PacingMode | null>(null);
  const [pacingError, setPacingError] = useState<string | null>(null);
  useEffect(() => {
    if (open) return;
    setRestPacingOverride(null);
    setPacingError(null);
  }, [open]);
  useEffect(() => {
    if (!open || wsParty) return;
    let cancelled = false;
    Promise.all([apiClient.getParty(), apiClient.getMyCharacter()])
      .then(([{ party: p }, character]) => {
        if (cancelled) return;
        setMyCharId(character?.id ?? null);
        if (!p) {
          setRestParty(null);
          return;
        }
        const members = p.members as Array<{ id: string; name: string }> | undefined;
        const leaderCharacterId = p.leaderCharacterId as string | undefined;
        setRestParty({
          id: p.id as string,
          leaderCharacterId,
          pacingMode: p.pacingMode as PacingMode | undefined,
          activeGameSessionId: p.activeGameSessionId as string | undefined,
          leaderName: members?.find((m) => m.id === leaderCharacterId)?.name,
        });
      })
      .catch(() => {
        // Keep the section hidden; nothing to show without party data.
      });
    return () => {
      cancelled = true;
    };
  }, [open, wsParty]);

  const party = wsParty ?? restParty;
  const myCharacterId = selectedCharacter?.id ?? myCharId;
  const isLeader = !!party && party.leaderCharacterId === myCharacterId;
  const sessionActive = !!party?.activeGameSessionId;
  // Editable everywhere: the live lobby socket carries the change when
  // connected, and apiClient.setPartyPacing (REST, via api-server ->
  // lobby-server) covers everywhere else (dashboard, in-game).
  // Single-flight guard for the REST pacing path: the WS path rides one
  // ordered socket, but independent REST PATCHes from rapid clicks can land
  // out of order. Only the response matching the latest click is allowed to
  // touch state, and a request is pending until it resolves.
  const pacingRequestId = useRef(0);
  const [pacingPending, setPacingPending] = useState(false);
  const canSetPacing = isLeader && !sessionActive && !pacingPending;
  const pacingMode: PacingMode = restPacingOverride ?? party?.pacingMode ?? 'turn_based';
  const leaderName =
    (wsParty
      ? wsParty.members?.find((m) => m.id === wsParty.leaderCharacterId)?.name
      : restParty?.leaderName) ?? 'the party leader';
  // Clicking a locked control must explain why nothing happened: a `disabled`
  // button swallows the click and leaves the player guessing.
  const handlePacingClick = (mode: PacingMode) => {
    if (pacingPending) return;
    if (!isLeader) {
      toast.warning(`Only ${leaderName}, your party leader, can change the pacing.`);
      return;
    }
    if (sessionActive) {
      toast.warning('Pacing can only be changed before the adventure starts.');
      return;
    }
    handleSetPacing(mode);
  };
  const handleSetPacing = (mode: PacingMode) => {
    setPacingError(null);
    if (lobbyOnline && wsParty) {
      websocketService.setPartyPacing(wsParty.id, mode);
      return;
    }
    const previous = restPacingOverride ?? party?.pacingMode ?? 'turn_based';
    const requestId = ++pacingRequestId.current;
    setPacingPending(true);
    setRestPacingOverride(mode);
    apiClient
      .setPartyPacing(mode)
      .catch((err: unknown) => {
        if (pacingRequestId.current !== requestId) return;
        setRestPacingOverride(previous);
        setPacingError(err instanceof Error ? err.message : 'Failed to change pacing');
      })
      .finally(() => {
        if (pacingRequestId.current === requestId) setPacingPending(false);
      });
  };

  // Friends management. Source of truth follows the CONNECTION, never the list
  // length: keying off `friends.length` would flip the source the moment the
  // last friend is removed, re-running the REST fetch and resurrecting the row
  // it just deleted. When the lobby socket is up it pushes `friend-removed`
  // and keeps the store live; everywhere else (dashboard, in-game) the REST
  // bridge answers, since presence lives in lobby memory.
  const wsFriends = useLobbyStore((state) => state.friends);
  const removeFriendFromStore = useLobbyStore((state) => state.removeFriend);
  const [restFriends, setRestFriends] = useState<FriendInfo[] | null>(null);
  const [removingFriendId, setRemovingFriendId] = useState<string | null>(null);
  const [confirmFriendId, setConfirmFriendId] = useState<string | null>(null);
  const friends = lobbyOnline ? wsFriends : (restFriends ?? []);

  useEffect(() => {
    if (!open) {
      setConfirmFriendId(null);
      return;
    }
    // FriendsPanel only mounts on /lobby, so the store can be cold here.
    if (lobbyOnline) {
      websocketService.requestFriendsList();
      return;
    }
    let cancelled = false;
    apiClient
      .getFriends()
      .then((list) => {
        if (!cancelled) setRestFriends(list);
      })
      .catch(() => {
        // Card stays hidden; nothing to manage without a list.
      });
    return () => {
      cancelled = true;
    };
  }, [open, lobbyOnline]);

  const handleRemoveFriend = (friend: FriendInfo) => {
    setRemovingFriendId(friend.userId);
    // Always the awaited REST call, even with the socket up: the WS
    // `friend-remove` frame is fire-and-forget, so resolving on send would
    // toast success and drop the row for a removal the server may have
    // rejected. When online, the server's `friend-removed` push updates the
    // store; the local drop below covers the offline path.
    apiClient
      .removeFriend(friend.userId)
      .then(() => {
        removeFriendFromStore(friend.userId);
        setRestFriends((prev) => prev?.filter((f) => f.userId !== friend.userId) ?? null);
        toast.success(`${friend.displayName} is no longer your friend.`);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Could not remove that friend');
      })
      .finally(() => {
        setRemovingFriendId(null);
        setConfirmFriendId(null);
      });
  };

  // Slider ships a 0..100 UI value; engine API is 0..1.
  const handleVolume = (value: number) => {
    soundtrackEngine.setVolume(value / 100);
  };

  // Per-account disconnect setting (what happens to the character when the
  // player drops past the grace period mid-session). Loaded from the profile
  // when the dialog opens; saved optimistically with revert on failure.
  const [dropBehavior, setDropBehavior] = useState<DropBehavior>('become_npc');
  const [combatHighlightsEnabled, setCombatHighlightsEnabled] = useState(true);
  const [combatHighlightsPending, setCombatHighlightsPending] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiClient
      .getMyProfile()
      .then((profile) => {
        if (!cancelled) {
          setDropBehavior(profile.dropBehavior ?? 'become_npc');
          setCombatHighlightsEnabled(profile.combatHighlightsEnabled);
        }
      })
      .catch(() => {
        // Keep the default; the card stays usable and a save still persists.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);
  const handleDropBehavior = (value: DropBehavior) => {
    const previous = dropBehavior;
    setDropBehavior(value);
    apiClient.patchMe({ dropBehavior: value }).catch(() => setDropBehavior(previous));
  };
  const handleCombatHighlights = (enabled: boolean) => {
    if (combatHighlightsPending) return;
    const previous = combatHighlightsEnabled;
    setCombatHighlightsEnabled(enabled);
    setCombatHighlightsPending(true);
    apiClient
      .patchMe({ combatHighlightsEnabled: enabled })
      .catch(() => {
        setCombatHighlightsEnabled(previous);
        toast.warning("Couldn't save the combat highlight setting.");
      })
      .finally(() => setCombatHighlightsPending(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Settings
          </DialogTitle>
          <DialogDescription className="sr-only">
            Adjust your display theme and audio preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Theme Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                Theme
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <Label>Appearance</Label>
                  <p className="text-sm text-muted-foreground">
                    {themePreference === 'system'
                      ? `Follow OS preference (currently ${theme})`
                      : 'Pinned regardless of OS preference'}
                  </p>
                </div>
                <div className="inline-flex rounded-md border bg-muted p-0.5">
                  {(
                    [
                      { value: 'light', label: 'Light', Icon: Sun },
                      { value: 'system', label: 'System', Icon: Monitor },
                      { value: 'dark', label: 'Dark', Icon: Moon },
                    ] as const
                  ).map(({ value, label, Icon }) => {
                    const active = themePreference === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setTheme(value)}
                        aria-pressed={active}
                        aria-label={`${label} theme`}
                        className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                          active
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audio Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                Audio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Global audio on/off — kill audio without zeroing volume */}
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <Label htmlFor="audio-enabled">Audio Playback</Label>
                  <p className="text-sm text-muted-foreground">
                    {audioOn ? 'On' : 'All audio muted.'}
                  </p>
                </div>
                <input
                  id="audio-enabled"
                  type="checkbox"
                  checked={audioOn}
                  onChange={(e) => soundtrackEngine.setAudioOn(e.target.checked)}
                  className="h-4 w-4 accent-primary cursor-pointer shrink-0"
                  aria-label="Audio Playback"
                />
              </div>

              {/* Soundtrack volume — disabled while audio is off */}
              <div className={audioOn ? undefined : 'opacity-50'}>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="audio-volume">Soundtrack volume</Label>
                  <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">
                    {Math.round(volume * 11)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <VolumeX className="h-4 w-4 text-muted-foreground shrink-0" />
                  <input
                    id="audio-volume"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round(volume * 100)}
                    onChange={(e) => handleVolume(Number(e.target.value))}
                    disabled={!audioOn}
                    className="w-full accent-primary"
                    aria-label="Volume"
                  />
                  <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Billed first-person combat clips (per-account) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clapperboard className="h-4 w-4" />
                Combat Highlights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <Label htmlFor="combat-highlights-enabled">First-person attack clips</Label>
                  <p className="text-sm text-muted-foreground">
                    {combatHighlightsEnabled
                      ? 'Occasionally generate a billed video of your hero’s damaging attacks, injuries, and the encounter-ending blow, shared with your party.'
                      : 'Off: your own actions won’t generate videos and no tokens are spent. You’ll still see clips your party generates.'}
                  </p>
                </div>
                <input
                  id="combat-highlights-enabled"
                  type="checkbox"
                  checked={combatHighlightsEnabled}
                  disabled={combatHighlightsPending}
                  onChange={(event) => handleCombatHighlights(event.target.checked)}
                  className="h-4 w-4 accent-primary cursor-pointer shrink-0 disabled:cursor-wait"
                  aria-label="Combat Highlights"
                />
              </div>
            </CardContent>
          </Card>

          {/* Disconnect behavior (per-account) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Unplug className="h-4 w-4" />
                Disconnection Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <Label>Mid-adventure drop</Label>
                  <p className="text-sm text-muted-foreground">
                    {dropBehavior === 'become_npc'
                      ? 'Your hero fights on (AI-controlled) until you return.'
                      : 'Your hero steps out of the scene until you return.'}
                  </p>
                </div>
                <div className="inline-flex rounded-md border bg-muted p-0.5">
                  {(
                    [
                      { value: 'become_npc', label: 'Fight On', Icon: Bot },
                      { value: 'leave_scene', label: 'Step Out', Icon: DoorOpen },
                    ] as const
                  ).map(({ value, label, Icon }) => {
                    const active = dropBehavior === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          if (!active) handleDropBehavior(value);
                        }}
                        aria-pressed={active}
                        aria-label={`${label} on disconnect`}
                        className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                          active
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Party Pacing (leader-controlled) */}
          {party && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Timer className="h-4 w-4" />
                  Pacing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label>Round pacing</Label>
                    <p className="text-sm text-muted-foreground">
                      {pacingMode === 'turn_based'
                        ? 'Turn-based: the world waits for players.'
                        : 'Real-time: the world advances on its own.'}
                      {!isLeader && ` Only ${leaderName}, your party leader, can change this.`}
                      {isLeader &&
                        sessionActive &&
                        ' Set before you start; locked during an adventure.'}
                      {pacingError && ` ${pacingError}`}
                    </p>
                  </div>
                  <div className="inline-flex rounded-md border bg-muted p-0.5">
                    {(
                      [
                        { value: 'turn_based', label: 'Turn-Based', Icon: Timer },
                        { value: 'real_time', label: 'Real-Time', Icon: Zap },
                      ] as const
                    ).map(({ value, label, Icon }) => {
                      const active = pacingMode === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            if (!active) handlePacingClick(value);
                          }}
                          aria-pressed={active}
                          aria-label={`${label} pacing`}
                          className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                            active
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground'
                          } ${canSetPacing ? (active ? '' : 'hover:text-foreground') : 'cursor-not-allowed opacity-60'}`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Friends (removal is mutual: neither side keeps the other) */}
          {friends.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Friends
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {friends.map((friend) => {
                  const confirming = confirmFriendId === friend.userId;
                  const busy = removingFriendId === friend.userId;
                  return (
                    <div
                      key={friend.userId}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
                    >
                      {friend.avatarUrl ? (
                        <img
                          src={friend.avatarUrl}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <span className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs shrink-0">
                          {friend.avatarEmoji ?? (
                            <UserRound className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </span>
                      )}
                      <span className="text-sm truncate flex-1">{friend.displayName}</span>
                      {confirming ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleRemoveFriend(friend)}
                            className="rounded px-2 py-1 text-xs font-medium bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-60"
                          >
                            {busy ? 'Removing...' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setConfirmFriendId(null)}
                            className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmFriendId(friend.userId)}
                          aria-label={`Remove ${friend.displayName} as a friend`}
                          title="Remove friend"
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:text-destructive shrink-0"
                        >
                          <UserMinus className="h-3.5 w-3.5" />
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground pt-1">
                  Removing a friend removes you from their list too, and closes any open chat.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
